import { useEffect, useState } from "react";
import type { EmailInboxSettings, EmailInboxView, EmailMessageDTO } from "../../../shared/types";
import { Avatar, Badge, Button, ErrorText, Field, inputCls, timeAgo } from "../../components/ui";
import { ChannelHeader } from "./Channels";
import { List, ListRow } from "../../components/List";
import { get, post, put } from "../../lib/api";
import { TextLink } from "../../components/TextLink";

const STATUS: Record<EmailMessageDTO["status"], { label: string; tone: "emerald" | "zinc" | "red" | "blue" }> = {
  processed: { label: "answered", tone: "emerald" },
  ignored: { label: "ignored", tone: "zinc" },
  failed: { label: "failed", tone: "red" },
  pending: { label: "pending", tone: "blue" },
};

function SettingsCard({ view, busy, onSave }: { view: EmailInboxView; busy: boolean; onSave: (s: Partial<EmailInboxSettings>) => void }) {
  const [address, setAddress] = useState(view.settings.address);
  const [domains, setDomains] = useState(view.settings.allowedDomains.join(", "));
  const [every, setEvery] = useState(String(view.settings.checkEverySeconds));
  return (
    <div className="card space-y-4 p-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field label="Pandas mailbox">
          <input className={inputCls} value={address} onChange={(e) => setAddress(e.target.value)} placeholder="pandas@pandas.io" />
        </Field>
        <Field label="Allowed sender domains" hint="Comma separated. Anyone else is ignored.">
          <input className={inputCls} value={domains} onChange={(e) => setDomains(e.target.value)} placeholder="pandas.io" />
        </Field>
        <Field label="Check every (seconds)">
          <input className={inputCls} type="number" min={30} max={3600} value={every} onChange={(e) => setEvery(e.target.value)} />
        </Field>
      </div>
      <div className="flex justify-end">
        <Button variant="primary" disabled={busy} onClick={() => onSave({ address, allowedDomains: domains.split(/[\s,]+/).filter(Boolean), checkEverySeconds: Number(every) })}>
          Save
        </Button>
      </div>
    </div>
  );
}

function MessageRow({ m }: { m: EmailMessageDTO }) {
  const status = STATUS[m.status];
  return (
    <ListRow
      leading={<Avatar name={m.fromName || m.fromEmail || "?"} />}
      title={
        <>
          <span className="text-sm font-medium">{m.fromName || m.fromEmail || "Unknown sender"}</span>
          {m.fromName && <span className="text-xs text-faint">{m.fromEmail}</span>}
          <Badge tone={status.tone}>{status.label}</Badge>
          {m.replyStatus === "sent" && <Badge tone="emerald">replied</Badge>}
          {m.replyStatus === "pending" && <Badge tone="blue">working on it</Badge>}
          {m.replyStatus === "failed" && <Badge tone="red">reply failed</Badge>}
        </>
      }
      subtitle={<span className="text-sm font-medium text-muted">{m.subject || "(no subject)"}</span>}
      trailing={<span className="text-faint">{timeAgo(m.receivedAt ?? m.createdAt)}</span>}
    >
      <div className="truncate text-sm text-muted">{m.snippet}</div>
      {(m.reason || m.replyError) && m.status !== "processed" && <div className="mt-1 text-xs text-muted">{m.reason}</div>}
      {m.replyError && <div className="mt-1 text-xs text-danger">{m.replyError}</div>}
      {m.conversationId && (
        <TextLink to={`/admin/conversations/${m.conversationId}`} className="mt-1 inline-block text-xs">
          Open conversation
        </TextLink>
      )}
    </ListRow>
  );
}

export function AdminEmail() {
  const [view, setView] = useState<EmailInboxView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => get<EmailInboxView>("/api/email").then(setView, (e) => setError(e.message));
  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 10_000);
    return () => clearInterval(t);
  }, []);

  const run = async (fn: () => Promise<EmailInboxView>) => {
    setBusy(true);
    setError(null);
    try {
      setView(await fn());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pb-10">
      {view && (
        <ChannelHeader
          brand="gmail"
          name="Email"
          subtitle="Colleagues email Pandas and get answers by email, in the same thread. Emails from people with a Pandas account become conversations with Pandas Bot, which runs with the sender's own access."
          actions={
            <Button disabled={busy || !view.settings.enabled} onClick={() => run(() => post<EmailInboxView>("/api/email/check"))}>
              Check now
            </Button>
          }
          on={view.settings.enabled}
          toggleDisabled={busy}
          onToggle={(enabled) => void run(() => put<EmailInboxView>("/api/email/settings", { enabled }))}
          offText={`Off: emails to ${view.settings.address} aren't answered.`}
        />
      )}
      <div className="space-y-5 px-4 md:px-6">
        {!view && <div className="pt-5" />}
        <ErrorText error={error} />
        {!view ? (
          <div className="card h-40 animate-pulse" />
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted">
              <span>
                {view.settings.address}
                {view.connector && ` · via ${view.connector}`}
                {view.lastCheckAt && ` · checked ${timeAgo(view.lastCheckAt)}`}
              </span>
            </div>
            {view.problem && <p className="rounded-xl bg-warning/10 px-3 py-2 text-sm text-warning">{view.problem}</p>}
            {view.lastError && <p className="rounded-xl bg-danger/10 px-3 py-2 text-sm text-danger">Last check failed: {view.lastError}</p>}
            <SettingsCard view={view} busy={busy} onSave={(s) => run(() => put<EmailInboxView>("/api/email/settings", s))} />
            <List
              title="Inbox"
              empty={
                <div className="flex flex-col items-center gap-1 py-2">
                  <img src="/favicon.png" alt="Pandito" className="mb-1 size-12 rounded-2xl shadow-sm ring-1 ring-line" />
                  <span className="text-sm font-medium text-fg">No emails yet</span>
                  <span className="text-xs text-muted">Emails sent to {view.settings.address} show up here once this is on.</span>
                </div>
              }
            >
              {view.messages.map((m) => <MessageRow key={m.id} m={m} />)}
            </List>
          </>
        )}
      </div>
    </div>
  );
}
